import { type LayeredMapBaseMap } from '~/_shared/types/map';
import { parseDate } from '~/_shared/utils/date/date.helpers';
import { createLayeringFromResponse } from '~/store/mapInfo/mapInfo.factory';
import {
  type MapListingItem, type SpreadsheetMapInfo,
} from './listing/item/mapListingItem';
import { getMapOwnerUserId } from './map.helpers';
import {
  type BaseMapsLayeringResponse, type MapInfoResponse, type MapSettingsKeyOf,
  type MapSettingsUpdateSettingsConcurrencyErrorResponse, type MapSnapshotResponse, type SpreadsheetDescriptorResponse,
} from './map.repository';
import { type MapSettingsUpdateSettingsConcurrencyErrorReason } from './settings/outOfSync/mapSettingsOutOfSync.types';

const createMapListingItemFromMapSnapshotResponse = (response: MapSnapshotResponse): MapListingItem => {
  return {
    id: response.id,
    name: response.name,
    mapViews: response.views,
    snapshots: [],
    spreadSheets: [],
    privacy: response.privacy,
    shareId: response.share_id,
    dateCreated: parseDate(response.created.date) || new Date(),
    dateUpdated: parseDate(response.updated.date) || new Date(),
    isLayered: false,
    isReadOnly: response.read_only,
    ownerId: getMapOwnerUserId(response),
    source: response.source ?? null,
    sharedUsers: response.shared_users,
  };
};

export const createMapListingItemFromMapBaseMapResponse = (
  response: BaseMapsLayeringResponse
): LayeredMapBaseMap | null => {
  if (!response.created) {
    return null;
  }

  return {
    dateCreated: parseDate(response.created.date) ?? new Date(),
    dateUpdated: (response.date_updated && parseDate(response.date_updated)) || undefined,
    id: response.id,
    isLayered: false,
    isReadOnly: response.read_only,
    mapViews: response.views,
    name: response.name,
    ownerId: getMapOwnerUserId({ created: response.created }),
    privacy: response.privacy,
    shareId: response.share_id,
    sharedUsers: response.shared_users,
    source: response.source,
    spreadsheets: response.spreadsheets?.map(s => ({
      spreadsheetId: s.spreadsheet_id,
      realSpreadsheets: s.real_spreadsheets.map(rS => ({
        realSpreadsheetId: rS.real_spreadsheet_id,
      })),
    })),
  };
};

const createRealSpreadSheetFromResponse = (response: ReadonlyArray<{real_spreadsheet_id: number}>): ReadonlyArray<{ realSpreadsheetId: number }> => {
  return response.map((item) => ({ realSpreadsheetId: item.real_spreadsheet_id }));
};

const createMapListingItemFromMapSpreadSheetResponse = (response: SpreadsheetDescriptorResponse): SpreadsheetMapInfo => {
  return {
    spreadsheetId: response.spreadsheet_id,
    isEncrypted: response.is_encrypted,
    isPrimary: response.is_primary,
    privileges: response.privileges,
    realSpreadsheets: createRealSpreadSheetFromResponse(response.real_spreadsheets),
    geocoding: response.geocoding,
  };
};

export const createMapListingItemFromMapResponse = (response: MapInfoResponse): MapListingItem => {
  const snapshots = (response.snapshots ?? []).map(snapshot => createMapListingItemFromMapSnapshotResponse(snapshot));
  const spreadSheets = response.spreadsheets.map(spreadSheets => createMapListingItemFromMapSpreadSheetResponse(spreadSheets));

  return {
    id: response.id,
    name: response.name,
    mapViews: response.views,
    snapshots,
    spreadSheets,
    dateCreated: parseDate(response.created.date) || new Date(),
    dateUpdated: parseDate(response.updated.date) || new Date(),
    isLayered: response.is_layered,
    layering: createLayeringFromResponse(response?.layering),
    isMatchupRequired: response.is_matchup_required,
    isImporting: response.importing,
    isReadOnly: response.read_only,
    privacy: response.privacy,
    shareId: response.share_id,
    sharedUsers: response.shared_users,
    ownerId: getMapOwnerUserId(response),
    source: response.source ?? null,
  };
};

export const createUpdateSettingsConcurrencyError = (
  response: MapSettingsUpdateSettingsConcurrencyErrorResponse
): MapSettingsUpdateSettingsConcurrencyErrorReason => {
  const reason: MapSettingsUpdateSettingsConcurrencyErrorReason = {};

  Object.entries(response.reason).forEach(([key, value]) => {
    reason[key as MapSettingsKeyOf] = {
      concurrencyCheck: value.concurency_check,
      timestamp: value.timestamp,
      userId: value.userId,
      userName: value.userName,
    };
  });

  return reason;
};
